
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// --- Constants (reusable) ---
const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;

const selectOptions = {
    effortLevel: ['None', 'Light', 'Moderate', 'Heavy'],
    workStyle: ['Online', 'Local', 'Hybrid'],
    location: ['Online', 'Local', 'Travel'],
    requiresVehicle: ['No', 'Yes'],
    visibilityType: ['Private', 'Public', 'Indirect'],
    incomeSpeed: ['Instant', 'Quick', 'Delayed'],
    techComfort: ['Low', 'Medium', 'High']
};

const initialFormState = {
    lessonTitle: '', lessonNumber: '', summary: '', link: '', level: 1,
    cashGPTLessons: '', minInvestment: 0, effortLevel: 'None', workStyle: 'Online',
    location: 'Online', requiresVehicle: 'No', visibilityType: 'Private',
    incomeSpeed: 'Quick', techComfort: 'Low', priority: 1, group: 21
};

// --- Main Component ---
function LessonFormModal({ mode = 'add', lessonId, isOpen, onClose, onSuccess }) {
    const [formData, setFormData] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const isEditMode = mode === 'edit';

    const fetchLessonData = useCallback(async () => {
        if (!lessonId) return;
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/lesson/${lessonId}`);
            if (response.data.success) {
                // Omit metadata fields before setting form data
                const { _id, createdAt, updatedAt, __v, name, ...lessonData } = response.data.data;
                setFormData(lessonData);
            }
        } catch (error) {
            setIsError(true);
            setMessage("Failed to load lesson data.");
            console.error("Fetch error:", error);
        } finally {
            setIsLoading(false);
        }
    }, [lessonId]);

    useEffect(() => {
        if (!isOpen) { // Reset on close
            setFormData(initialFormState);
            setMessage('');
            setIsError(false);
            return;
        }

        if (isEditMode) {
            fetchLessonData();
        } else {
            setFormData(initialFormState);
        }
    }, [isOpen, isEditMode, fetchLessonData]);


    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const finalValue = type === 'number' ? Number(value) : value;
        setFormData(prev => ({ ...prev, [name]: finalValue }));
        setMessage('');
        setIsError(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setMessage('');
        setIsError(false);

        try {
            const response = isEditMode
                ? await axios.put(`${API_BASE_URL}/update-lesson/${lessonId}`, formData)
                : await axios.post(`${API_BASE_URL}/add-lesson`, formData);

            const successMsg = response.data.message || `Lesson ${isEditMode ? 'updated' : 'added'} successfully!`;
            setMessage(successMsg);
            setIsError(false);

            if (onSuccess) {
                onSuccess(successMsg, mode);
            }
            setTimeout(onClose, 1500);

        } catch (error) {
            const errorMessage = error.response?.data?.error || 'An unexpected error occurred.';
            setMessage(errorMessage);
            setIsError(true);
            console.error('Submit error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-5 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">
                        {isEditMode ? 'Edit Lesson' : 'Add New Lesson'}
                    </h2>
                    <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                
                {/* Form Content */}
                <div className="overflow-y-auto flex-1 p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* The form structure remains the same as your provided code */}
                        {/* ... all the input fields, textareas, and select menus ... */}
                        {/* I'm pasting your excellent form structure here directly */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Text Inputs */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Lesson Title <span className="text-red-500">*</span></label>
                                <input type="text" name="lessonTitle" value={formData.lessonTitle} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required placeholder="Enter lesson title" />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Lesson Number <span className="text-red-500">*</span></label>
                                <input type="text" name="lessonNumber" value={formData.lessonNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required placeholder="E.g: 01 or E01" />
                            </div>
                            {/* ... continue with all other inputs ... */}
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Link <span className="text-red-500">*</span></label><input type="url" name="link" value={formData.link} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required placeholder="https://example.com" /></div>
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Level <span className="text-red-500">*</span></label><input type="number" name="level" value={formData.level} onChange={handleChange} min="1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required /></div>
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Priority <span className="text-red-500">*</span></label><input type="number" name="priority" value={formData.priority} onChange={handleChange} min="1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required /></div>
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Group <span className="text-red-500">*</span></label><input type="number" name="group" value={formData.group} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required /></div>
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Min Investment ($) <span className="text-red-500">*</span></label><div className="relative"><span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span><input type="number" name="minInvestment" value={formData.minInvestment} onChange={handleChange} min="0" className="w-full pl-8 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required /></div></div>
                            {Object.entries(selectOptions).map(([key, options]) => (<div key={key} className="space-y-2"><label className="block text-sm font-medium text-gray-700 capitalize">{key.replace(/([A-Z])/g, ' $1')} <span className="text-red-500">*</span></label><select name={key} value={formData[key]} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white appearance-none" required>{options.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>))}
                        </div>
                        <div className="space-y-6">
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">Summary <span className="text-red-500">*</span></label><textarea name="summary" value={formData.summary} onChange={handleChange} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required placeholder="Brief description of the lesson..." /></div>
                            <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">CashGPT Lessons (comma-separated) <span className="text-red-500">*</span></label><textarea name="cashGPTLessons" value={formData.cashGPTLessons} onChange={handleChange} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required placeholder="Lesson1, Lesson2, Lesson3..." /></div>
                        </div>

                        {message && (<div className={`p-3 rounded-lg ${isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{message}</div>)}
                        
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button type="button" onClick={onClose} disabled={isLoading} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">Cancel</button>
                            <button type="submit" disabled={isLoading} className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                                {isLoading ? (<span>Saving...</span>) : (isEditMode ? 'Update Lesson' : 'Save Lesson')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default LessonFormModal;